import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faUser,
  faEnvelope,
  faPhone,
  faMobile,
  faUserTag,
  // faCalendarCheck,
  faCalendarDay,
  faIdBadge,
  faUserShield,
  faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

/**
 * Small presentational row used inside `UserViewModal`.
 * Shows an icon, a label and a value; can be highlighted.
 */
const InfoRow = ({ icon, label, value, highlight = false }) => {
  const { t } = useTranslation();
  const emDash = t('common.symbols.emDash');
  const displayValue = value || emDash;
  return (
    <div
      className="flex items-start justify-between rounded-xl border px-4 py-3"
      style={{
        borderColor: UI_COLORS.BORDERS.LIGHT,
        backgroundColor: highlight
          ? colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1)
          : UI_COLORS.BACKGROUNDS.CARD,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-lg"
          style={{
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.3),
            color: UI_COLORS.TEXT.PRIMARY,
          }}
        >
          <FontAwesomeIcon icon={icon} className="text-base" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
            {label}
          </p>
          <p className="text-base font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {displayValue}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Compute inline styles for the status pill based on active/inactive.
 * Returns an object with `backgroundColor` and `color` keys.
 */
const getStatusStyles = (isActive) => {
  if (isActive) {
    return {
      backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.1),
      color: UI_COLORS.STATUS.SUCCESS,
    };
  }
  return {
    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
    color: UI_COLORS.STATUS.ERROR,
  };
};

/**
 * Format an ISO datetime string into a locale string; returns em-dash on invalid input.
 * @param {string|Date} value
 * @returns {string}
 */
const formatDateTime = (value, emDash) => {
  if (!value) return emDash;
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return emDash;
  }
};

/**
 * Modal that displays read-only user information.
 * - Renders a loading state, an empty state, or the user details.
 */
const UserViewModal = ({ isOpen, onClose, user, loading = false }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const strings = t('users.view', { returnObjects: true });
  const statusStrings = t('users.table.statuses', { returnObjects: true });
  const emDash = t('common.symbols.emDash');
  const userName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || strings.header.fallbackTitle : strings.header.fallbackTitle;
  const roleValue = user?.user_type || emDash;
  const statusLabel = user?.is_active ? statusStrings.active : statusStrings.inactive;
  const createdAtLabel = formatDateTime(user?.created_at, emDash);
  const phoneValue = user?.phone_number || emDash;
  const mobileValue = user?.mobile_number || emDash;
  const emailValue = user?.email || emDash;
  const firstNameValue = user?.first_name || emDash;
  const lastNameValue = user?.last_name || emDash;
  const userIdValue = user?.id || user?.user_id || emDash;
  const defaultWarehouseValue = user?.default_warehouse_name || user?.default_warehouse_alias || user?.default_warehouse_id || emDash;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <style>{`.user-view-modal{ -ms-overflow-style: none; scrollbar-width: none; } .user-view-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div
        className="user-view-modal w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-5"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {strings.header.sectionTitle}
            </p>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {userName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent"
                style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}
              ></div>
              <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.loading}
              </p>
            </div>
          ) : !user ? (
            <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <FontAwesomeIcon
                icon={faUserShield}
                className="text-4xl opacity-60"
                style={{ color: UI_COLORS.TEXT.MUTED }}
              />
              <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {strings.empty.title}
              </h3>
              <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.empty.message}
              </p>
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <InfoRow icon={faUser} label={strings.fields.firstName} value={firstNameValue} />
                <InfoRow icon={faUser} label={strings.fields.lastName} value={lastNameValue} />
                <InfoRow icon={faEnvelope} label={strings.fields.email} value={emailValue} />
                <InfoRow icon={faUserTag} label={strings.fields.role} value={roleValue} highlight />
                <InfoRow icon={faPhone} label={strings.fields.phoneNumber} value={phoneValue} />
                <InfoRow icon={faMobile} label={strings.fields.mobileNumber} value={mobileValue} />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div
                  className="rounded-xl border px-4 py-3"
                  style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                >
                  <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                    {strings.fields.status}
                  </p>
                  <div className="mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold"
                    style={getStatusStyles(user.is_active)}
                  >
                    <span>{statusLabel}</span>
                  </div>
                </div>

                <InfoRow
                  icon={faIdBadge}
                  label={strings.fields.userId}
                  value={userIdValue}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <InfoRow icon={faCalendarDay} label={strings.fields.createdAt} value={createdAtLabel} />
                <InfoRow 
                  icon={faBuilding} 
                  label={strings.fields.defaultWarehouse} 
                  value={defaultWarehouseValue} 
                  highlight={!!user?.default_warehouse_id}
                />
              </div>
            </>
          )}
        </div>

        <div
          className="flex items-center justify-end gap-3 border-t px-6 py-4"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <button
            onClick={onClose}
            className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: 'transparent',
              color: UI_COLORS.TEXT.MUTED,
              border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
            }}
          >
            {strings.buttons.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserViewModal;
